import { Avatar, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import IconButton from '@mui/material/IconButton';
import { appUrl, formatNumber, post } from '../../utils/fetch.js';
import './VideoPreview.css';
import TubeSubmit from '../form/TubeSubmit.js';
import { useEffect, useRef, useState } from 'react';
import { edit as editVideo } from '../../slices/videoSlice.js';
import Hls from 'hls.js'; 

function VideoPreview(props) {
    const { sx, userDetail } = props;
    const videoData = useSelector((state) => state.video);
    const video = useRef();
    const dispatch = useDispatch();
    const [ isLoadingVideoThumbnail, setIsLoadingVideoThumbnail ] = useState(false);
    const [ isLoadingCustomVideoThumbnail, setIsLoadingCustomVideoThumbnail ] = useState(false);
    const [ source, setSource ] = useState({
        source: "",
        url: ""
    });
    const [ time, setTime ] = useState(new Date());
    const [ hls, setHls ] = useState(null);
    
    useEffect(() => {
        if(videoData?.source){
            let sourceTemp = videoData?.source.split(";");
            setSource({
                source: sourceTemp[0],
                url: sourceTemp[1]
            });
        }     
    }, [videoData?.source]);

    // Initialize video element on mount
    useEffect(() => {
        if (video.current) {
            // Clean up any existing state on mount
            cleanupVideoElement(video.current);
        }
    }, []);

    // Function to check if URL is M3U8/HLS
    const isHlsUrl = (url) => {
        return url && (url.includes('.m3u8') || url.toLowerCase().includes('m3u8'));
    };

    // Clean up video element and remove all tracks/subtitles
    const cleanupVideoElement = (videoElement) => {
        if (!videoElement) return;
        
        // Remove all text tracks (subtitles, captions, etc.)
        const tracks = videoElement.textTracks;
        for (let i = tracks.length - 1; i >= 0; i--) {
            if (tracks[i].mode !== 'disabled') {
                tracks[i].mode = 'disabled';
            }
        }
        
        // Clear any existing source
        videoElement.src = '';
        videoElement.load();
        
        // Remove any source elements if they exist
        const sources = videoElement.querySelectorAll('source');
        sources.forEach(source => source.remove());
        
        // Remove any track elements if they exist
        const trackElements = videoElement.querySelectorAll('track');
        trackElements.forEach(track => track.remove());
    };

    // Function to setup HLS player
    const setupHlsPlayer = (videoElement, url) => {
        // Ensure element is clean before setting up HLS
        cleanupVideoElement(videoElement);
        
        if (Hls.isSupported()) {
            const hlsInstance = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 90,
                // Disable subtitle loading by default
                subtitleTrackController: false,
                subtitleDisplay: false
            });
            
            hlsInstance.loadSource(url);
            hlsInstance.attachMedia(videoElement);
            setHls(hlsInstance);
            
            hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
                console.log('HLS manifest loaded, ready to play');
                // Auto-play is usually blocked by browsers, so we don't auto-play
            });
            
            hlsInstance.on(Hls.Events.ERROR, (event, data) => {
                console.error('HLS error:', data);
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            console.log('Fatal network error encountered, try to recover');
                            hlsInstance.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            console.log('Fatal media error encountered, try to recover');
                            hlsInstance.recoverMediaError();
                            break;
                        default:
                            console.log('Fatal error, cannot recover');
                            hlsInstance.destroy();
                            setHls(null);
                            break;
                    }
                }
            });
            
            return hlsInstance;
        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            // For Safari which has native HLS support
            videoElement.src = url;
            console.log('Using native HLS support');
            return null;
        } else {
            console.error('HLS is not supported in this browser');
            // Fallback: try to load the URL directly (might work for some formats)
            videoElement.src = url;
            return null;
        }
    };

    // Cleanup HLS instance on component unmount or video change
    useEffect(() => {
        const videoElement = video.current;
        return () => {
            if (hls) {
                hls.destroy();
                setHls(null);
            }
            // Also cleanup video element on unmount
            if (videoElement) {
                cleanupVideoElement(videoElement);
            }
        };
    }, [hls]);

    // Setup video player when video data changes
    useEffect(() => {
        if (!video.current) return;
        
        // Always cleanup first
        if (hls) {
            hls.destroy();
            setHls(null);
        }
        
        // Clean up the video element
        cleanupVideoElement(video.current);
        
        // Short delay to ensure cleanup is complete
        const timeoutId = setTimeout(() => {
            if (!video.current) return;
            
            if (videoData?.external_url && isHlsUrl(videoData.external_url)) {
                // Setup HLS player for M3U8 files
                setupHlsPlayer(video.current, videoData.external_url);
            } else if (videoData?.external_url) {
                // For non-HLS external URLs, set source directly
                video.current.src = videoData.external_url;
            } else if (videoData?.file) {
                // For internal files
                video.current.src = `${appUrl}/videos/${videoData.file}`;
            }
        }, 100);
        
        return () => {
            clearTimeout(timeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoData?.external_url, videoData?.file, videoData?.id]);
    
    return (
        <div style={{...sx,
            maxWidth: "50%"
        }} className={`video-preview`}>
            <div className='video-card'>
                <Grid
                    container 
                    spacing={0}
                >
                    <Grid
                        item
                        md={1}
                    >
                        <Avatar 
                            alt="Dummy User"
                            src={`${appUrl}/${videoData?.userAvatar}`}
                        >
                            {videoData?.userInitials}
                        </Avatar>
                    </Grid>
                    <Grid
                        item
                        md={10}
                    >
                        <div className='video-card-user-name'>
                            {videoData?.user}
                        </div>
                        <div className='video-card-user-registration'>
                            Registered: {videoData?.userCreatedAt}
                        </div>
                    </Grid>
                    <Grid
                        item
                        md={1}
                    >
                        <IconButton className='video-card-arrow' onClick={userDetail}>
                            <ArrowForwardIcon fontSize={"inherit"} />
                        </IconButton>
                    </Grid>
                    <Grid
                        item
                        md={12}
                        className={"video-card-line"}
                    >
                        <div />
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Video ID</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{videoData?.id}</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Views</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{formatNumber(videoData?.views)}</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Date published</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{videoData?.datePublished}</div>
                    </Grid>
                    <Grid
                        item
                        md={12}
                        className={"video-card-line"}
                    >
                        <div />
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Resolution</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{videoData && videoData?.videoData ?  videoData?.videoData?.dimensions : null}</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Length</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{videoData?.length}</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Video FPS</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{videoData?.fps}</div>
                    </Grid>
                    <Grid
                        item
                        md={12}
                        className={"video-card-line"}
                    >
                        <div />
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-name'>Imported from</div>
                    </Grid>
                    <Grid
                        item
                        md={6}
                    >
                        <div className='video-card-data-value'>{source.source ? source.source : "Direct upload"}</div>
                    </Grid>
                    {source.url ? (
                        <>
                            <Grid
                                item
                                md={6}
                            >
                                <div className='video-card-data-name'>Original URL</div>
                            </Grid>
                            <Grid
                                item
                                md={6}
                            >
                                <div className='video-card-data-value'><a href={source.url} target="_blank" rel="noreferrer">{source.url}</a></div>
                            </Grid>
                        </>                        
                    ) : ""}
                </Grid>
            </div>
            <div className='title-14' style={{
                marginTop: 20,
                marginBottom: 5
            }}>
                Video Preview
            </div>
            {videoData?.embed ? (
                <iframe 
                    className={"video-file"}
                    src={videoData?.embed}
                    title="Video Player"
                    frameborder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowfullscreen
                />
            ) : (
                <video
                    controls
                    ref={video}
                    className={"video-file"}
                    key={`${videoData?.id}-${videoData?.external_url ? 'external' : 'internal'}`}
                />
            )}
            <div className='title-14' style={{
                marginTop: 20,
                marginBottom: 5
            }}>
                Video thumbnail
            </div>
            <Grid container spacing={2}>
                <Grid item>
                    {videoData?.external_url ? (
                        <div className={`video-preview-thumbnail ${videoData?.vertical ? 'vertical' : ''} external-url-placeholder`}>
                            <div>{isHlsUrl(videoData.external_url) ? 'HLS Stream' : 'External URL'}</div>
                        </div>
                    ) : (
                        <video
                            src={`${appUrl}/videos/thumbs/preview-${videoData?.file}`}
                            className={`video-preview-thumbnail ${videoData?.vertical ? 'vertical' : ''}`}
                            autoPlay
                            loop
                            width={150}
                            height={85}
                        />
                    )}
                </Grid>
                <Grid item>
                    <div
                        style={{backgroundImage: videoData?.thumbnail === "no-preview" ? "none" : `url('${appUrl}/videos/thumbs/${videoData?.thumbnail}?t=${time}')`}}
                        className={`video-preview-thumbnail ${videoData?.vertical ? 'vertical' : ''}`}
                    >
                        {videoData?.thumbnail === "no-preview" && (
                            <div>
                                Encoding...
                            </div>
                        )}    
                    </div>
                </Grid>
                <Grid item>
                    {!videoData?.embed && !videoData?.external_url && (
                        <Grid item md={12} sx={{paddingBottom: "5px"}}>
                            <TubeSubmit
                                label="Set current preview frame"
                                variant={"outlined"}
                                isLoading={isLoadingVideoThumbnail}
                                onClick={() => post(`${appUrl}/api/thumbnail-video`, {
                                    videoFile: videoData?.file,
                                    thumbnail: videoData?.thumbnail,
                                    currentTime: video.current.currentTime
                                }, (result) => {
                                    setIsLoadingVideoThumbnail(false);
                                    setTime(new Date());
                                    dispatch(editVideo({thumbnail: result.data.thumbnail}));
                                }, () => setIsLoadingVideoThumbnail(true))}
                            />
                        </Grid>
                    )}
                    {!videoData?.external_url && (
                        <Grid item md={12}>
                            <TubeSubmit
                                label={(
                                    <>
                                        Upload custom thumbnail
                                        <input
                                            type="file"
                                            onChange={(e) => post(`${appUrl}/api/custom-thumbnail-video`, {
                                                thumbnail: videoData?.thumbnail,
                                                file: e.target.files[0]
                                            }, (result) => {
                                                setIsLoadingCustomVideoThumbnail(false);
                                                setTime(new Date());
                                                dispatch(editVideo({thumbnail: result.data.thumbnail}));
                                            }, () => setIsLoadingCustomVideoThumbnail(true))}
                                            hidden
                                            id={`input-custom-thumbnail`}
                                        />
                                    </>
                                )}
                                variant={"outlined"}
                                isLoading={isLoadingCustomVideoThumbnail}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </div>
    );
}

export default VideoPreview;

VideoPreview.propTypes = {
    sx: PropTypes.object,
};